import { useQuery } from '@tanstack/react-query'
import { useCompleteAssessment } from '@/features/test-series/hooks/useTestSeries'
import solutionBrowserService from '../services/solutionBrowserService'
import type { SolutionViewItem, PaperbackSubject } from '../types'

const PAPERBACK_SUBJECTS: PaperbackSubject[] = ['PHYSICS', 'CHEMISTRY', 'BIOLOGY']

/**
 * Fetches batch solution data for the given question IDs.
 * Only runs when `enabled` is true (i.e., after assessment data resolves).
 */
function useBatchSolutions(questionIds: string[], enabled: boolean, userId?: string) {
  return useQuery({
    queryKey: ['batch-solutions', questionIds, userId],
    queryFn: () => solutionBrowserService.getBatchSolutions(questionIds, userId),
    staleTime: 10 * 60 * 1000, // 10 minutes — solutions don't change
    enabled: enabled && questionIds.length > 0,
  })
}

/**
 * Composes assessment metadata + solution data into a merged SolutionViewItem[].
 *
 * Steps:
 * 1. Fetch complete assessment by testCode (sections + question order)
 * 2. Collect all questionIds in order
 * 3. Fetch batch solutions (enabled only after step 1 resolves)
 * 4. Merge into SolutionViewItem[] preserving question order
 */
export function useSolutionBrowserData(testCode: string, userId?: string) {
  const assessmentQuery = useCompleteAssessment(testCode)

  const assessment = assessmentQuery.data?.data

  // Build ordered list of question IDs from all sections
  const orderedQuestions = assessment?.sections
    ? assessment.sections
        .slice()
        .sort((a, b) => a.sectionOrder - b.sectionOrder)
        .flatMap((section) =>
          section.questions
            .slice()
            .sort((a, b) => a.questionNumber - b.questionNumber)
            .map((q) => ({
              questionId: q.questionId,
              questionNumber: q.questionNumber,
              sectionName: section.sectionName,
              topicCode: q.topicCode,
              marks: q.marks,
              negativeMarks: q.negativeMarks,
            }))
        )
    : []

  // Derive which paperback subjects are present in this test
  const testSubjects: PaperbackSubject[] = assessment?.sections
    ? ([
        ...new Set(
          assessment.sections
            .map((s) => s.sectionType)
            .filter((t): t is PaperbackSubject => PAPERBACK_SUBJECTS.includes(t as PaperbackSubject))
        ),
      ] as PaperbackSubject[])
    : []

  const questionIds = orderedQuestions.map((q) => q.questionId)
  const assessmentReady = !!assessment && orderedQuestions.length > 0

  const solutionsQuery = useBatchSolutions(questionIds, assessmentReady, userId)

  const solutionsMap = new Map(
    (solutionsQuery.data?.data?.solutions ?? []).map((s) => [
      s.questionId,
      { solutionData: s.solutionData, testCode: s.testCode },
    ])
  )

  const items: SolutionViewItem[] = orderedQuestions.map((q) => ({
    ...q,
    testCode: solutionsMap.get(q.questionId)?.testCode,
    solutionData: solutionsMap.get(q.questionId)?.solutionData,
  }))

  const isLoading = assessmentQuery.isLoading || (assessmentReady && solutionsQuery.isLoading)
  const error = assessmentQuery.error ?? solutionsQuery.error

  return { items, isLoading, error, testName: assessment?.testName, testSubjects, assessmentLoaded: !!assessment }
}
